import { Button, Grid, Tab, Tabs } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Project, ProjectType } from '../../../models/project';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { NotesDialog } from '../../note/notes.dialog';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { SiTrackPage } from '../../../components/si-track-page';
import { ContactsForm } from '../project-forms/contacts.form';
import { FeaturesSetup } from '../project-forms/features-setup.component';
import { FormButtons } from '../project-forms/form-buttons.component';
import { LocationForm } from '../project-forms/location.form';
import { ProjectHeaderForm } from '../project-forms/project-header.form';
import { RecidentialCommunityForm } from '../project-forms/recidential-community.form';
import { RecidentialSingleLotForm } from '../project-forms/recidential-single-lot.form';
import { ServicesForm } from '../project-forms/services.form';
import { TimeAndMaterialForm } from '../project-forms/time-and-material.form';
import { ProjectInfoComponent } from '../project-forms/project-info.component';
import { ProctorResultForm } from '../project-details/project-proctor-result/proctor-results.form';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { useLoadData } from '../../../hooks/useLoadData';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { log } from 'util';
import { TabPanel } from '../../../components/tab-panel';
import { EditProjectBuildingTab } from '../edit-project/edit-project-building.tab';

const projectValidationSchema = Yup.object().shape({
  // customer: Yup.object().shape({ customerID: Yup.string().required('Customer is required') }),
  // office: Yup.object().shape({ customerID: Yup.string().required('Office is required') }),
  projectName: Yup.string().required('Project name is required').max(21, 'Project name cannot exceed 21 characters'),
  // street: Yup.string().required('Street address is required'),
  zipcode: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable(),
  projectDivisions: Yup.array().test(
    'is-not-empty',
    'Division is required',
    (value) => value !== undefined && value.length > 0 // Trigger error if undefined or empty array
  ),
  projectType: Yup.string().required('Project type is required '),
  projectSubTypes: Yup.array().test(
    'is-not-empty',
    'Project subtype is required',
    (value) => value !== undefined && value.length > 0 // Trigger error if undefined or empty array
  ),
  departments: Yup.object()
    .shape({
      departmentID: Yup.string()
        .nullable()
        .required('Department is required')
        .test('not-zero', 'Department is required', (value) => value !== '' && value !== '0'),
    })
    .required('Department is required'),
  ffServiceCat: Yup.string().required('Fastfield service cat is required'),
  projectCode: Yup.string().required('Project code is required'),
  projectCommunityType: Yup.string().required('Project type is required'),
  customer: Yup.object()
    .shape({
      customerID: Yup.string()
        .nullable()
        .required('Customer is required')
        .test('not-zero', 'Customer is required', (value) => value !== '' && value !== '0'),
    })
    .required('Customer  is required'),
  office: Yup.object()
    .shape({
      officeID: Yup.string()
        .nullable()
        .required('Office is required')
        .test('not-zero', 'Office is required', (value) => value !== '' && value !== '0'),
    })
    .nullable()
    .required('Office  is required'),
  multipleLotsCount: Yup.string().when(['projectLotType', 'projectCommunityType'], {
    is: (projectLotType: string, projectCommunityType: string) =>
      projectLotType === ProjectType.CommunityMultipleLot || projectCommunityType === ProjectType.CommunityScatteredLot,
    then: (schema) => schema.required('How many lots is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  // singleLotInfo: Yup.string().when(['projectCommunityType'], {
  //   is: (projectCommunityType: string) => projectCommunityType === ProjectType.ResidentialSingleLot,
  //   then: (schema) => schema.required('Lot info is required'),
  //   otherwise: (schema) => schema.notRequired(),
  // }),
  // singleLotAddress: Yup.string().when(['projectCommunityType'], {
  //   is: (projectCommunityType: string) => projectCommunityType === ProjectType.ResidentialSingleLot,
  //   then: (schema) => schema.required('Address is required'),
  //   otherwise: (schema) => schema.notRequired(),
  // }),

  projectLotType: Yup.string().when(['projectCommunityType'], {
    is: (projectCommunityType: string) => projectCommunityType === ProjectType.ResidentialCommunity,
    then: (schema) => schema.required('Lot type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const CreateProjectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'true';

  const requestType = searchParams.get('reqtype');
  const requestID = searchParams.get('reqid');

  const {
    isLoading: isProjectRequestLoading,
    refetch: projectRequestRefetch,
    data: projectRequestData,
  } = useLoadData<ApiResponse<Project>>(projectsApiKeyStore.getProjectRequestDetails(requestID, requestType));

  const [isChange, setIsChange] = useState<boolean>(!!searchParams.get('customerID') && !!searchParams.get('officeID'));
  const [projectDetails, setProjectDetails] = useState<any>();

  const form = useReactHookForm({
    defaultValues: {
      customer: !searchParams.get('customerID')
        ? null
        : { customerID: Number(searchParams.get('customerID') ?? ''), customerName: '' },
      office: !searchParams.get('officeID')
        ? null
        : { officeID: Number(searchParams.get('officeID') ?? 0), officeName: '' },
      // projectName: "",
      // description: "",
      // street: "",
      // state: "",
      // zipcode: "",
      // reqtype: "",
      // projectType: "",
      rebates: '',
      rebateCompanies: { utilRebateCompanyID: '', utilRebateCompanyName: '' },
      departments: null,
      projectCommunityType: '',
      projectLotType: '',
      builderCode: '',
      sendInsReport: false,
      syncWithDux: false,
      coCRequired: false,
      includeHVACGrading: false,
    },
    yupSchema: projectValidationSchema,
  });

  useEffect(() => {
    if (projectRequestData?.data && requestID) {
      const projectRequestDetails: any = projectRequestData?.data;
      form.setValue('projectName', projectRequestDetails?.projectName);
      form.setValue('description', projectRequestDetails?.description);
      form.setValue('street', projectRequestDetails?.street);
      form.setValue('projectType', projectRequestDetails?.projectType);
      form.setValue('state', {
        stateID: projectRequestDetails?.state ? projectRequestDetails?.state : 0,
        stateName: '',
      });
      form.setValue('zipcode', projectRequestDetails?.zipcode);
      form.setValue('reqtype', requestType);
      form.setValue('reqid', requestID);
      setProjectDetails(projectRequestData?.data);
    }
  }, [projectRequestData]);

  const [projectProps, setProjectProps] = useState({
    customerID: Number(searchParams.get('customerID') ?? 0),
    officeID: Number(searchParams.get('officeID') ?? 0),
    projectID: 0,
    planTempEntityID: '',
  });
  const [rebateValue, setRebateValue] = useState<string>('');
  const [isCustomerLoading, setCustomerLoading] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<Partial<any>>();
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [noteTempEntityID, setNoteTempEntityID] = useState<number>();
  const [planTempEntityID, setPlanTempEntityID] = useState<string>();
  const [officeChange, setOfficeChange] = useState<boolean>(!!searchParams.get('officeID'));
  const [projectContactList, setProjectContactList] = useState<any[]>([]);
  const { isLoading, mutateAsync } = useSaveData<Project>(projectsApiKeyStore.createProject());

  const watchFieldsValueProjectName = form.watch('projectName');
  const watchFieldsValueCustomer = form.watch('customer');
  const watchFieldsValueProjectType = form.watch('projectType');
  const watchFieldsValueProjectDivisions = form.watch('projectDivisions');
  const watchFieldsValueOffice = form.watch('office');
  const watchFieldsValueMultipleLotsCount = form.watch('multipleLotsCount');
  const watchFieldValueProjectCommunityType = form.watch('projectCommunityType');
  const watchFieldValueSingleLotInfo = form.watch('singleLotInfo');
  const watchFieldValueSingleLotAddress = form.watch('singleLotAddress');
  const watchFieldValueFfServiceCat = form.watch('ffServiceCat');
  const watchFieldValueFfStreet = form.watch('street');
  const watchFieldValueFfProjectCode = form.watch('projectCode');
  const watFieldValueProjectSubTypes = form.watch('projectSubTypes');

  useEffect(() => {
    if (watchFieldsValueProjectName) {
      form.trigger();
    }
  }, [
    watchFieldsValueProjectName,
    watchFieldsValueCustomer,
    watchFieldsValueProjectType,
    watchFieldsValueProjectDivisions,
    watchFieldsValueOffice,
    watchFieldsValueMultipleLotsCount,
    watchFieldValueProjectCommunityType,
    watchFieldValueSingleLotInfo,
    watchFieldValueSingleLotAddress,
    watchFieldValueFfServiceCat,
    watchFieldValueFfStreet,
    watchFieldValueFfProjectCode,
    watFieldValueProjectSubTypes,
  ]);

  const handleOpenNoteDialog = () => {
    setOpenNoteDialog(!openNoteDialog);
  };

  const handleCloseNoteDialog = (tempProjectEntityID?: number) => {
    setOpenNoteDialog(!openNoteDialog);
    if (tempProjectEntityID) {
      setNoteTempEntityID(tempProjectEntityID);
    }
  };

  const isViewUite = useAuthorize([UserPermissionName.ADD_UNIT]);

  const { data } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceMappingList(
      0,
      !projectProps?.projectID ? form.getValues('office.officeID') : 0,
      !projectProps?.projectID ? 0 : projectProps?.projectID,
      0,
      0
    )
  );

  useEffect(() => {
    if (data && projectProps?.officeID) {
      data?.data?.map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          form.setValue('dryRunFee', service.price);
        } else if (service.serviceCode === 'Late Cancellation') {
          form.setValue('lateCancellationFee', service.price);
        } else if (service.serviceCode === 'Trip Charge') {
          form.setValue('tripCharge', service.price);
        }
      });
      // Toggle state to trigger re-render
    }
  }, [data, projectProps?.officeID]);

  const checkServiceDuplicate = (services: any) => {
    const deduplicatedServices = services?.reduce(
      (acc: any, current: any) => {
        if (!acc.map?.has(current?.serviceID)) {
          acc?.map?.set(current?.serviceID, true);
          acc?.result?.push({
            ...current,
            isActive: true, // Set isActive to true
          });
        }
        return acc;
      },
      { map: new Map(), result: [] }
    ).result;

    return deduplicatedServices;
  };

  const handleSubmit = async (project: any) => {
    const filterData = data?.data
      ?.filter((d) => d.isNotReadyService)
      .map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          return {
            ...service,
            price: project?.dryRunFee,
            invoiceRetestAmount: project?.dryRunFee,
            accrualAmount: project?.dryRunFee,
            accrualRetestAmount: project?.dryRunFee,
          };
        } else if (service.serviceCode === 'Late Cancellation') {
          return {
            ...service,
            price: project?.lateCancellationFee,
            invoiceRetestAmount: project?.lateCancellationFee,
            accrualAmount: project?.lateCancellationFee,
            accrualRetestAmount: project?.lateCancellationFee,
          };
        } else if (service.serviceCode === 'Trip Charge') {
          return {
            ...service,
            price: project?.tripCharge,
            invoiceRetestAmount: project?.tripCharge,
            accrualAmount: project?.tripCharge,
            accrualRetestAmount: project?.tripCharge,
          };
        }
        return service; // Return the service unchanged if no condition matches
      });
    const newData = {
      ...project,
      customerID: projectProps.customerID,
      officeID: projectProps.officeID,
      state: project.state?.stateID,
      multipleLotsCount: Number(project.multipleLotsCount),
      projectPlans: project.projectPlans?.map((m: any) => m.planID),
      projectMunicipalities: project.projectMunicipalities?.map((m: any) => m.municipalityID),
      marketAreas: project.marketAreas?.map((m: any) => m.marketAreaID),
      planReviews: project.planReviews?.map((p: any) => p.planReviewTypeID),
      projectDivisions: project.projectDivisions?.map((m: any) => m.divisionID),
      projectContacts: project.projectContacts?.map((m: any) => m.contactsID),
      projectDivisionInfo:
        project?.sendEmail?.length > 0
          ? project?.sendEmail?.map((id: any) => ({
              divisionID: id,
              sendReminderEmail: true,
            }))
          : [],
      projectContactsList:
        projectContactList?.length > 0
          ? projectContactList?.map((p: any) => {
              return {
                contactsID: p.contactsID,
                customerID: project?.customerID ?? 0,
                officeID: project?.officeID ?? 0,
                projectID: project?.projectID ?? 0,
                syncWithDux: project?.syncWithDux ? p.syncWithDux : false,
                isFastFieldCM: p?.isFastFieldCM ?? false,
                contactsNotificationIDs:
                  p?.contactsNotificationIDs?.length > 0
                    ? p?.contactsNotificationIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                ppiReportStatusIDs:
                  p?.ppiReportStatusIDs?.length > 0
                    ? p?.ppiReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                susReportStatusIDs:
                  p?.susReportStatusIDs?.length > 0
                    ? p?.susReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                otherReportStatusIDs:
                  p?.otherReportStatusIDs?.length > 0
                    ? p?.otherReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
              };
            })
          : project?.projectContacts?.map((p: any) => {
              return {
                contactsID: p.contactsID,
                customerID: project?.customerID ?? 0,
                officeID: project?.officeID ?? 0,
                projectID: project?.projectID ?? 0,
                syncWithDux: project?.syncWithDux ? p.syncWithDux : false,
                isFastFieldCM: p?.isFastFieldCM ?? false,
                contactsNotificationIDs:
                  p?.contactsNotificationIDs?.length > 0
                    ? p?.contactsNotificationIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                ppiReportStatusIDs:
                  p?.ppiReportStatusIDs?.length > 0
                    ? p?.ppiReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                susReportStatusIDs:
                  p?.susReportStatusIDs?.length > 0
                    ? p?.susReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
                otherReportStatusIDs:
                  p?.otherReportStatusIDs?.length > 0
                    ? p?.otherReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                    : [],
              };
            }),
      projectProctorResults: project?.projectProctorResults?.map((pr: any) => pr.proctorResultsID),
      projectCoordinators: project.projectCoordinators?.map((p: any) => p.userID),
      projectSubTypes: project.projectSubTypes?.map((ps: any) => ps.projectSubTypeID),
      rebateCompanies: project?.rebateCompanies?.utilRebateCompanyID
        ? [project?.rebateCompanies?.utilRebateCompanyID]
        : [],
      planTempEntityID: planTempEntityID ? planTempEntityID : null,
      ...(project.multipleLotsCount && { multipleLotsCount: Number(project.multipleLotsCount) }),
      noteTempEntityID,
      projectServices: checkServiceDuplicate([...(project?.projectServices || []), ...(filterData || [])]),
      generateWorkOrders: project?.buttonType == 'with-workorder',
      includeHVACGrading: project.includeHVACGrading ?? false,
      departmentID: project?.departments?.departmentID ?? 0,
    };

    const result = await mutateAsync({
      data: newData,
    });

    if (
      project.projectType === ProjectType.TimeAndMaterial ||
      project.projectLotType === ProjectType.CommunityMultipleLot ||
      project.projectLotType === ProjectType.Buildings ||
      project.projectCommunityType === ProjectType.Buildings ||
      project.projectCommunityType === ProjectType.CommunityScatteredLot ||
      project.projectCommunityType === ProjectType.ResidentialSingleLot
    ) {
      if (!isViewUite) {
        navigate(`/projects`);
      } else {
        navigate(`/projects/edit/${result.data.projectID}?tab=buildings`);
      }
    }
  };

  const handleProjectProps = (type: string, value: any) => {
    if (type == 'officeID' && value != null) setOfficeChange(true);
    else setOfficeChange(false);
    setProjectProps((prevState) => ({
      ...prevState,
      [type]: value,
    }));
  };

  const handleCustomerLoading = (value: boolean) => {
    setCustomerLoading(value);
  };

  const handleRebateValue = (rebate: string) => {
    setRebateValue(rebate);
  };

  const handleProjectContactList = (projectContacts: any[]) => {
    setProjectContactList(projectContacts ?? []);
  };

  const { showSnackbar } = useSnackbar();

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const handleFormSubmit = (buttonType: string) =>
    form.handleSubmit((values) => handleSubmit({ ...values, buttonType }), handleError)();

  const isProjectRequest = requestType === 'mysi' && !!requestID;

  const currentTabValue: 'details' | 'buildings' = searchParams.get('tab') as any;
  const [currentTab, setCurrentTab] = useState<'details' | 'buildings'>(currentTabValue ?? 'details');
  const handleTabsChange = (event: any, value: any) => {
    setCurrentTab(value);
    projectRequestRefetch();
  };
  const isViewUnitPermission = useAuthorize([UserPermissionName.VIEW_UNIT]);

  const projectRequestDetails = {
    requestType: requestType,
    requestID: requestID,
  };

  const formContent = (
    <ReactHookForm formName="PROJECT_FORM" onSubmit={() => {}} formContextProps={form} isBusy={isLoading}>
      <Grid container spacing={1.5}>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <ProjectHeaderForm
            changeProps={handleProjectProps}
            onCustomerLoading={handleCustomerLoading}
            isChange={isChange}
            onRebateValue={handleRebateValue}
          />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <ProjectInfoComponent
            isCustomerLoading={isCustomerLoading}
            isRequestLoading={isProjectRequestLoading}
            rebateValue={rebateValue}
          />
        </Grid>
        <Grid style={{ display: 'none' }} item lg={12} sm={12} xl={12} xs={12}>
          <RecidentialSingleLotForm />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <RecidentialCommunityForm />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <TimeAndMaterialForm />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <LocationForm isRequestLoading={isProjectRequestLoading} />
        </Grid>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE_PROJECT]}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ServicesForm name="projectServices" id={0} page="Project" pageAction="create" />
          </Grid>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ContactsForm
              name="projectContacts"
              label="Contacts"
              page="create"
              updateContactList={handleProjectContactList}
            />
          </Grid>
        </GuardedComponent>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <ProctorResultForm name="projectProctorResults" label="Proctor Results" />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <FeaturesSetup openNoteDialog={handleOpenNoteDialog} />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <FormButtons
            isLoading={isLoading}
            page="create"
            handleCancel={() => {
              navigate('/projects');
            }}
            handleSubmit={handleFormSubmit}
            isRequestType={isProjectRequest}
            requestID={requestID ?? ''}
          />
        </Grid>
      </Grid>
    </ReactHookForm>
  );

  return (
    <SiTrackPage title="Create Project">
      {isProjectRequest ? (
        <>
          <Tabs value={currentTab} onChange={handleTabsChange}>
            <Tab value="details" label="PROJECT INFO" />
            <Tab value="buildings" label="LOTS/UNITS" hidden={!isViewUnitPermission} />
          </Tabs>
          <TabPanel selectedValue={currentTab} value="details">
            {formContent}
          </TabPanel>
          {isViewUnitPermission && (
            <TabPanel selectedValue={currentTab} value="buildings">
              <EditProjectBuildingTab
                project={projectDetails}
                projectID={0}
                projectRequestDetails={projectRequestDetails}
              />
              <Grid container justifyContent="space-between" spacing={1}>
                <Grid item>
                  <Button
                    variant="outlined"
                    style={{ width: '130px' }}
                    onClick={() => {
                      navigate('/projects');
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </TabPanel>
          )}
        </>
      ) : (
        formContent
      )}
      <NotesDialog
        title="PROJECT"
        noteRequestArgs={{ noteTempEntityID }}
        open={openNoteDialog}
        onClose={handleCloseNoteDialog}
      />
    </SiTrackPage>
  );
};
